import * as React from "react";
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    Skeleton,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import { LineChart } from "@mui/x-charts/LineChart";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { ExpandableCard } from "shared/ui/ComposedCards";
import EmptyState from "shared/ui/EmptyState";
import { useTranslation } from "shared/hooks/useTranslation";
import { useDollarRates, type DateRange } from "features/market/hooks/useDollarRates";
import { DOLLAR_TYPES, type DollarQuote, type DollarType } from "features/market/api/DollarRates";

const TYPE_COLORS: Record<DollarType, string> = {
    oficial: "#2DDA7D",
    blue: "#4D8DFF",
    bolsa: "#B65CFF",
    cripto: "#FF4DA6",
};

const BITCOIN_COLOR = "#F7931A";

function formatCurrency(value: number): string {
    return `$ ${value.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatUsd(value: number, locale: string): string {
    return `US$ ${value.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function getMonthLabels(locale: string): string[] {
    const fmt = new Intl.DateTimeFormat(locale, { month: "short" });
    return Array.from({ length: 12 }, (_, i) => fmt.format(new Date(2000, i, 1)));
}

function buildYearOptions(): number[] {
    const now = new Date().getFullYear();
    const years: number[] = [];
    for (let y = now - 10; y <= now; y++) years.push(y);
    return years;
}

const compactSelectSx = {
    minWidth: 72,
    flex: 1,
    "& .MuiInputBase-root": { height: 34, fontSize: "0.8rem" },
    "& .MuiInputLabel-root": { fontSize: "0.8rem" },
};

interface DateRangeFilterProps {
    dateRange: DateRange;
    onChange: (range: DateRange) => void;
}

function DateRangeFilter({ dateRange, onChange }: DateRangeFilterProps) {
    const { t, currentLanguage } = useTranslation();
    const monthLabels = React.useMemo(() => getMonthLabels(currentLanguage), [currentLanguage]);
    const yearOptions = React.useMemo(buildYearOptions, []);

    const fromMonth = dateRange.from.getMonth();
    const fromYear = dateRange.from.getFullYear();
    const toMonth = dateRange.to.getMonth();
    const toYear = dateRange.to.getFullYear();

    const updateFrom = (month: number, year: number) => {
        const next = new Date(year, month, 1);
        if (next > dateRange.to) return;
        onChange({ ...dateRange, from: next });
    };
    const updateTo = (month: number, year: number) => {
        const next = new Date(year, month + 1, 0);
        if (next < dateRange.from) return;
        onChange({ ...dateRange, to: next });
    };

    return (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
            <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 36 }}>{t("investments.from")}</Typography>
            <Stack direction="row" spacing={0.5} sx={{ flex: 1 }}>
                <FormControl size="small" sx={compactSelectSx}>
                    <InputLabel>{t("analytics.month")}</InputLabel>
                    <Select value={fromMonth} onChange={(event) => updateFrom(event.target.value as number, fromYear)} input={<OutlinedInput label={t("analytics.month")} />} MenuProps={{ disableScrollLock: true }}>
                        {monthLabels.map((label, index) => <MenuItem key={label} value={index}>{label}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={compactSelectSx}>
                    <InputLabel>{t("analytics.year")}</InputLabel>
                    <Select value={fromYear} onChange={(event) => updateFrom(fromMonth, event.target.value as number)} input={<OutlinedInput label={t("analytics.year")} />} MenuProps={{ disableScrollLock: true }}>
                        {yearOptions.map((year) => <MenuItem key={year} value={year}>{year}</MenuItem>)}
                    </Select>
                </FormControl>
            </Stack>
            <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 30 }}>{t("investments.to")}</Typography>
            <Stack direction="row" spacing={0.5} sx={{ flex: 1 }}>
                <FormControl size="small" sx={compactSelectSx}>
                    <InputLabel>{t("analytics.month")}</InputLabel>
                    <Select value={toMonth} onChange={(event) => updateTo(event.target.value as number, toYear)} input={<OutlinedInput label={t("analytics.month")} />} MenuProps={{ disableScrollLock: true }}>
                        {monthLabels.map((label, index) => <MenuItem key={label} value={index}>{label}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={compactSelectSx}>
                    <InputLabel>{t("analytics.year")}</InputLabel>
                    <Select value={toYear} onChange={(event) => updateTo(toMonth, event.target.value as number)} input={<OutlinedInput label={t("analytics.year")} />} MenuProps={{ disableScrollLock: true }}>
                        {yearOptions.map((year) => <MenuItem key={year} value={year}>{year}</MenuItem>)}
                    </Select>
                </FormControl>
            </Stack>
        </Stack>
    );
}

function sampleLabels(labels: string[], maxTicks: number): (string | null)[] {
    if (labels.length <= maxTicks) return labels;
    const step = Math.ceil(labels.length / maxTicks);
    return labels.map((label, index) => (index % step === 0 || index === labels.length - 1) ? label : null);
}

interface DollarRatesCardProps {
    density?: "page" | "panel";
    viewFullHistoryHref?: string;
}

export default function DollarRatesCard({
    density = "page",
    viewFullHistoryHref,
}: DollarRatesCardProps = {}) {
    const { t, currentLanguage } = useTranslation();
    const isPanel = density === "panel";
    const {
        rates,
        bitcoinPrice,
        bitcoinLoading,
        filteredData,
        loading,
        error,
        selectedType,
        setSelectedType,
        dateRange,
        setDateRange,
    } = useDollarRates();
    const referenceQuote = rates?.bolsa;
    const bitcoinUsd = bitcoinPrice?.usd;
    const bitcoinArs = bitcoinUsd != null && referenceQuote ? bitcoinUsd * referenceQuote.venta : null;

    const chartDates = loading || error || !rates ? [] : filteredData.map((item) => item.fecha);
    const chartBuy = filteredData.map((item) => item.compra);
    const chartSell = filteredData.map((item) => item.venta);
    const tickLabels = sampleLabels(chartDates, 8);

    const chartContent = loading || error || !rates ? undefined : (
        <Box sx={{ px: { xs: 1, sm: 2 }, pb: 2 }}>
            <Stack spacing={2} sx={{ mt: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <ToggleButtonGroup
                        size="small"
                        value={selectedType}
                        exclusive
                        onChange={(_, value) => value && setSelectedType(value as DollarType)}
                    >
                        {DOLLAR_TYPES.map((type) => (
                            <ToggleButton key={type} value={type}>
                                {t(`investments.${type}`)}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>

                <DateRangeFilter dateRange={dateRange} onChange={setDateRange} />

                {filteredData.length > 0 ? (
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <LineChart
                            height={300}
                            xAxis={[{
                                scaleType: "point" as const,
                                data: chartDates,
                                tickLabelStyle: { angle: -30, textAnchor: "end", fontSize: 11 },
                                valueFormatter: (value: string, context: { location: string }) => {
                                    if (context.location === "tick") {
                                        const index = chartDates.indexOf(value);
                                        return tickLabels[index] ?? "";
                                    }
                                    return value;
                                },
                            }]}
                            series={[
                                { id: "sell", label: t("investments.sell"), data: chartSell, color: TYPE_COLORS[selectedType], showMark: false },
                                { id: "buy", label: t("investments.buy"), data: chartBuy, color: `${TYPE_COLORS[selectedType]}88`, showMark: false },
                            ]}
                            margin={{ bottom: 60 }}
                        />
                    </Box>
                ) : (
                    <Typography color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                        {t("investments.loading")}
                    </Typography>
                )}
            </Stack>
        </Box>
    );

    return (
        <>
            <Paper
                elevation={0}
                sx={{
                    width: "100%",
                    overflow: "hidden",
                    p: isPanel ? 1.75 : { xs: 2.25, sm: 3 },
                    borderRadius: isPanel ? 3 : 4,
                    bgcolor: "background.paper",
                    backgroundImage: (theme) => theme.palette.mode === "dark"
                        ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.22)}, ${alpha(theme.palette.background.paper, 0.94)} 62%)`
                        : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.background.paper, 0.99)} 56%)`,
                    border: (theme) => `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.22 : 0.16)}`,
                    boxShadow: (theme) => theme.palette.mode === "dark"
                        ? `0 18px 44px ${alpha(theme.palette.common.black, 0.28)}`
                        : `0 18px 40px ${alpha(theme.palette.common.black, 0.08)}`,
                }}
            >
                {loading ? (
                    <DollarRatesSkeleton title={t("investments.referenceDollar")} />
                ) : error || !rates || !referenceQuote ? (
                    <EmptyState
                        icon={<ErrorOutlineRoundedIcon />}
                        title={t("investments.dollarRatesErrorTitle")}
                        body={t("investments.error")}
                    />
                ) : (
                    <Stack spacing={2}>
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) auto" }, gap: 2, alignItems: "start" }}>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography color="text.secondary" sx={{ fontWeight: 850 }}>
                                    {t("investments.referenceDollar")}
                                </Typography>
                                <Typography
                                    sx={{
                                        mt: 0.6,
                                        color: "primary.main",
                                        fontWeight: 950,
                                        fontSize: "clamp(2.15rem, 10vw, 3.4rem)",
                                        lineHeight: 1,
                                        fontVariantNumeric: "tabular-nums",
                                        overflowWrap: "anywhere",
                                    }}
                                >
                                    {formatCurrency(referenceQuote.venta)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.9, fontWeight: 700 }}>
                                    {t("investments.referenceDollarHelp")}
                                </Typography>
                            </Box>
                            <Box sx={{ textAlign: { xs: "left", sm: "right" }, minWidth: 128 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 800 }}>
                                    {t("investments.buy")}
                                </Typography>
                                <Typography sx={{ fontWeight: 900 }}>{formatCurrency(referenceQuote.compra)}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1, fontWeight: 800 }}>
                                    {t("investments.lastUpdate")}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 850 }}>{referenceQuote.fecha}</Typography>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: isPanel
                                    ? "repeat(2, minmax(0, 1fr))"
                                    : { xs: "1fr 1fr", md: "repeat(4, minmax(0, 1fr))" },
                                gap: 1,
                                pt: 1.5,
                                borderTop: (theme) => `1px solid ${alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.1 : 0.08)}`,
                            }}
                        >
                            {DOLLAR_TYPES.map((type) => (
                                <DollarMiniQuote
                                    key={type}
                                    type={type}
                                    quote={rates[type]}
                                />
                            ))}
                        </Box>

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: isPanel
                                    ? "repeat(2, minmax(0, 1fr))"
                                    : { xs: "1fr 1fr", md: "repeat(2, minmax(0, 1fr))" },
                                gap: 1,
                                pt: 1.5,
                                borderTop: (theme) => `1px solid ${alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.1 : 0.08)}`,
                            }}
                        >
                            {bitcoinLoading ? (
                                <>
                                    <Skeleton variant="rounded" height={86} sx={{ borderRadius: 3 }} />
                                    <Skeleton variant="rounded" height={86} sx={{ borderRadius: 3 }} />
                                </>
                            ) : bitcoinUsd != null ? (
                                <>
                                    <BitcoinMiniQuote
                                        label={t("investments.bitcoinUsd")}
                                        value={formatUsd(bitcoinUsd, currentLanguage)}
                                        subLabel={t("investments.bitcoin")}
                                    />
                                    <BitcoinMiniQuote
                                        label={t("investments.bitcoinArsEstimate")}
                                        value={bitcoinArs != null ? formatCurrency(bitcoinArs) : t("investments.bitcoinUnavailable")}
                                        subLabel={t("investments.bitcoinArsHelp")}
                                    />
                                </>
                            ) : (
                                <Box sx={{ gridColumn: "1 / -1" }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                                        {t("investments.bitcoinUnavailable")}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Stack>
                )}
            </Paper>

            {!isPanel && !loading && !error && rates && (
                <ExpandableCard title={t("investments.dollarHistory")}>{chartContent}</ExpandableCard>
            )}

            {isPanel && viewFullHistoryHref && !loading && !error && rates && (
                <Button
                    component={RouterLink}
                    to={viewFullHistoryHref}
                    size="small"
                    sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 800, borderRadius: 999, px: 1.5 }}
                >
                    {t("secondaryPanel.viewDollarHistory")}
                </Button>
            )}
        </>
    );
}

function BitcoinMiniQuote({ label, value, subLabel }: { label: string; value: string; subLabel: string }) {
    return (
        <Box
            sx={{
                minWidth: 0,
                p: 1.25,
                borderRadius: 3,
                bgcolor: alpha(BITCOIN_COLOR, 0.1),
                border: `1px solid ${alpha(BITCOIN_COLOR, 0.18)}`,
            }}
        >
            <Typography variant="caption" sx={{ display: "block", color: BITCOIN_COLOR, fontWeight: 950, textTransform: "uppercase" }}>
                {label}
            </Typography>
            <Typography sx={{ mt: 0.35, fontWeight: 950, fontSize: { xs: 16, sm: 18 }, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>
                {value}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.35 }}>
                {subLabel}
            </Typography>
        </Box>
    );
}

function DollarMiniQuote({ type, quote }: { type: DollarType; quote: DollarQuote }) {
    const { t } = useTranslation();
    const color = TYPE_COLORS[type];
    return (
        <Box
            sx={{
                minWidth: 0,
                p: 1.25,
                borderRadius: 3,
                bgcolor: alpha(color, 0.1),
                border: `1px solid ${alpha(color, 0.18)}`,
            }}
        >
            <Typography variant="caption" sx={{ display: "block", color, fontWeight: 950, textTransform: "uppercase" }}>
                {t(`investments.${type}`)}
            </Typography>
            <Typography sx={{ mt: 0.35, fontWeight: 950, fontSize: { xs: 16, sm: 18 }, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>
                {formatCurrency(quote.venta)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.35 }}>
                {t("investments.buy")}: {formatCurrency(quote.compra)}
            </Typography>
        </Box>
    );
}

function DollarRatesSkeleton({ title }: { title: string }) {
    return (
        <Stack spacing={2} role="status" aria-live="polite" aria-busy="true">
            <Box>
                <Typography color="text.secondary" sx={{ fontWeight: 850 }}>
                    {title}
                </Typography>
                <Skeleton width="72%" height={64} sx={{ mt: 0.6 }} />
                <Skeleton width="56%" height={22} />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                {[0, 1, 2, 3].map((item) => (
                    <Skeleton key={item} variant="rounded" height={86} sx={{ borderRadius: 3 }} />
                ))}
            </Box>
        </Stack>
    );
}
